
import { Injectable, inject } from "@angular/core";
import _ from "lodash";
import { Cellstyle } from "~app/common/components/hot-doc/hotdoc/cell";
import { TradslagExternkod } from "~app/common/models/constants";
import { RuleErrorContext, RuleSeverityLevel, RuleValidationMessage, RuleVisibilityStrategy } from "~app/common/services/cqs/models/rule-validation-message";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { VKSnabbkalkyl } from "../../models/tradslag/snabbkalkyl-models";
import { VKForkalkylTradslagHDCellStyles } from "../../models/tradslag/tradslag-models";
import { AtgardMediatorService } from "../atgard-mediator.service";
import { KontraktMediatorService } from "../kontrakt-mediator.service";
import { TradslagMediatorService } from "../tradslag-mediator.service";
import { VirkeskopValidationService } from "./virkeskop-validation.service";

@Injectable()
export class ForkalkylSnabbkalkylClientValidationService {
    private readonly validationService = inject(VirkeskopValidationService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly tradslagMediatorService = inject(TradslagMediatorService);

    private snabbkalkyl: VKSnabbkalkyl;

    clearValidations() {
        const validationKeys = [
            "totalAmountForkalkyl",
            "anyRowTimmerForkalkyl",
            "anyRowKlenGrovForkalkyl",
            "anyOver100Forkalkyl",
            "uttagM3FubMissing",
            "mstamMissing"
        ];

        validationKeys.forEach( validationKey => {
            this.validationService.updateClientSideValidation(
                false,
                this.createValidationMessage({
                    messageId: validationKey
                })
            );
        });
    }

    updateValidationTableFields(templateModel?: VKSnabbkalkyl): [Map<string, VKForkalkylTradslagHDCellStyles>, boolean] {
        this.snabbkalkyl = this.tradslagMediatorService.snabbkalkyl;

        const resultingValidation = new Map<string, VKForkalkylTradslagHDCellStyles>([]);
        if(_.isNil(this.snabbkalkyl) || this.snabbkalkyl.snabbkalkylTradslag.length === 0) {
            return [
                resultingValidation,
                false
            ];
        }

        let totalAmount = 0;
        this.snabbkalkyl.snabbkalkylTradslag.forEach((elem) => {
            totalAmount += elem.andel ?? 0;
            resultingValidation.set(elem.tradslag.namn, {});
        });

        let anyRowTimmerInvalid = false;
        let anyRowKlenGrovInvalid = false;
        let anyMstamMissing = false;
        let anyOver100 = false;
        let rowAmount: number;

        this.snabbkalkyl.snabbkalkylTradslag.forEach((elem) => {
            // För trädslaget Gran ska summan av Timmer, MassaV, GmaV och BränsleV vara 100
            if (elem.tradslag.namn === TradslagExternkod.Gran) {
                rowAmount = (elem.timmerandel ?? 0) + (elem.massavedandel ?? 0) + (elem.branslevedandel ?? 0) + (elem.granmassavedsandel ?? 0);

                if((rowAmount !== 100)) {
                    anyRowTimmerInvalid ||= true;
                    resultingValidation.get(elem.tradslag.namn).timmerandel = Cellstyle.Red;
                    resultingValidation.get(elem.tradslag.namn).massavedandel = Cellstyle.Red;
                    resultingValidation.get(elem.tradslag.namn).branslevedandel = Cellstyle.Red;
                    resultingValidation.get(elem.tradslag.namn).granmassavedsandel = Cellstyle.Red;
                }

            // För övriga trädslag ska summan av Timmer, MassaV och BränsleV vara 100
            } else {
                rowAmount = (elem.timmerandel ?? 0) + (elem.massavedandel ?? 0) + (elem.branslevedandel ?? 0);

                if((rowAmount !== 100)) {
                    anyRowTimmerInvalid ||= true;
                    resultingValidation.get(elem.tradslag.namn).timmerandel = Cellstyle.Red;
                    resultingValidation.get(elem.tradslag.namn).massavedandel = Cellstyle.Red;
                    resultingValidation.get(elem.tradslag.namn).branslevedandel = Cellstyle.Red;
                }
            }

            rowAmount = (elem.varavKlentimmer ?? 0) + (elem.varavGrovtimmer ?? 0);

            if((rowAmount > 100 || rowAmount < 0)) {
                anyRowKlenGrovInvalid ||= true;
                resultingValidation.get(elem.tradslag.namn).varavKlentimmer = Cellstyle.Red;
                resultingValidation.get(elem.tradslag.namn).varavGrovtimmer = Cellstyle.Red;
            }

            anyOver100 ||= (
                elem.varavKlentimmer > 100 ||
                elem.varavGrovtimmer > 100 ||
                elem.andel > 100 ||
                elem.timmerandel > 100 ||
                elem.massavedandel > 100 ||
                elem.branslevedandel > 100);

            if(!elem.medelstam) {
                anyMstamMissing = true;
                resultingValidation.get(elem.tradslag.namn).medelstam = Cellstyle.Red;
            }
        });

        const totalAmountInvalid = totalAmount !== 100;
        if(totalAmountInvalid) {
            this.snabbkalkyl.snabbkalkylTradslag.forEach((elem) => {
                resultingValidation.get(elem.tradslag.namn).andel = Cellstyle.Red;
            });
        }

        this.validationService.updateClientSideValidation(
            anyOver100,
            this.createValidationMessage({
                messageId: "anyOver100Forkalkyl",
                message: "Du kan inte ange mer än 100%",
                severity: RuleSeverityLevel.warning
            })
        );

        this.validationService.updateClientSideValidation(
            totalAmountInvalid,
            this.createValidationMessage({
                messageId: "totalAmountForkalkyl",
                message: "Summan av trädslagen måste vara 100%",
                severity: RuleSeverityLevel.warning
            })
        );

        this.validationService.updateClientSideValidation(
            anyRowTimmerInvalid,
            this.createValidationMessage({
                messageId: "anyRowTimmerForkalkyl",
                message: "Summan av fördelningen av  \"Timmer\",  \"MassaV\" och \"BränsleV\" kan inte vara större eller mindre än 100%",
                severity: RuleSeverityLevel.warning
            })
        );

        this.validationService.updateClientSideValidation(
            anyRowKlenGrovInvalid,
            this.createValidationMessage({
                messageId: "anyRowKlenGrovForkalkyl",
                message: "Summan av fördelningen av \"Kubb/KlenT\", \"GrovT\" kan inte vara större än 100%",
                severity: RuleSeverityLevel.warning
            })
        );
        const uttagM3FubMissing = ObjectUtils.isNullOrUndefined(this.snabbkalkyl.uttagM3FubHa);
        this.validationService.updateClientSideValidation(
            uttagM3FubMissing,
            this.createValidationMessage({
                messageId: "uttagM3FubMissing",
                message: "Uttag M3FUB/HA saknar värde",
                severity: RuleSeverityLevel.warning
            })
        );
        this.validationService.updateClientSideValidation(
            anyMstamMissing,
            this.createValidationMessage({
                messageId: "mstamMissing",
                message: "Medelstam (mstam) saknar värde",
                severity: RuleSeverityLevel.warning
            })
        );

        const anyValidationErrors = anyOver100 || anyRowKlenGrovInvalid || anyRowTimmerInvalid || totalAmountInvalid || uttagM3FubMissing || anyMstamMissing;

        this.applyTemplateColour(this.snabbkalkyl, templateModel, resultingValidation);

        return [
            resultingValidation,
            anyValidationErrors
        ];
    }

    private applyTemplateColour(snabbkalkyl: VKSnabbkalkyl, templateModel: VKSnabbkalkyl, resultingValidation: Map<string, VKForkalkylTradslagHDCellStyles>) {
        const tradslagsProperties = [
            "andel",
            "medelstam",
            "timmerandel",
            "massavedandel",
            "branslevedandel",
            "granmassavedsandel",
            "varavGrovtimmer",
            "varavKlentimmer"
        ];

        if(_.isNil(templateModel) || templateModel.snabbkalkylTradslag.length === 0) {
            return;
        }

        snabbkalkyl.snabbkalkylTradslag.forEach((elem) => {
            const snabbkalkylTradslag = templateModel.snabbkalkylTradslag.find(x => x.tradslag.id === elem.tradslag.id);
            if (_.isNil(snabbkalkylTradslag)) {
                return;
            }

            // Loopar alla egenskaper och lägger till blå.
            tradslagsProperties.forEach(prop => {
                if (elem[prop] === snabbkalkylTradslag[prop]) {
                    // eslint-disable-next-line no-bitwise
                    resultingValidation.get(elem.tradslag.namn)[prop] |= Cellstyle.Blue;
                }
            });
            if (elem.tradslag.id === snabbkalkylTradslag.tradslag.id) {
                // eslint-disable-next-line no-bitwise
                resultingValidation.get(elem.tradslag.namn).tradslag |= Cellstyle.Blue;
            }

        });

    }

    private get kontrakt() {
        return this.kontraktMediatorService.kontrakt;
    }

    private get atgardGuid() {
        return this.atgardMediatorService.atgard.guid;
    }

    private createValidationMessage(message: Partial<RuleValidationMessage>): RuleValidationMessage {
        return {
            severity: RuleSeverityLevel.info,
            strategy: RuleVisibilityStrategy.alwaysShow,
            context: RuleErrorContext.save,
            entityType: "tradslag",
            entityGuids: [
                this.kontrakt.guid,
                this.atgardGuid
            ],
            message: "",
            messageId: "",
            ...message
        };
    }

}
